import { CommonModule } from '@angular/common';
import { Component, HostListener, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FilterDrawerComponent } from '../../../components/filter-drawer/filter-drawer.component';
import {
  FilterDrawerFieldConfig,
  FilterDrawerValue,
} from '../../../components/filter-drawer/filter-drawer.models';
import { WorkflowCreationModalComponent } from '../workflow-creation-modal/workflow-creation-modal.component';
import { ApiServiceService } from '../../../modules/services/api-service.service';
import {
  MdaListResponse,
  MdaOption,
} from '../../../modules/services/utility-service.models';
import { GenericResponse, Workflow, WorkflowList } from '../../../modules/shared/models/general.modal';
import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import alertify from 'alertifyjs';
import { DropdownMenu } from "../../../components/dropdown-menu/dropdown-menu.component";
import { UnderscorePipe } from '../../../modules/pipes/underscore-pipe.pipe';
import { AuthenticationService } from '../../../modules/services/authentication.service';

type WorkflowSearchFilter = {
  mdaId: string;
  workflowName: string;
  fromDate: string;
  toDate: string;
};

@Component({
  selector: 'app-workflow-landing',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    WorkflowCreationModalComponent,
    FilterDrawerComponent,
    DropdownMenu,
    UnderscorePipe
],
  templateUrl: './workflow-landing.component.html',
  styleUrl: './workflow-landing.component.scss',
})
export class WorkflowLandingComponent implements OnInit {
  onToggleStatus(wf: Workflow): void {
    this.openMenuIndex = null;
    console.log('Toggle workflow status:', wf.id);
  }
  isModalOpen = false;
  isFilterDrawerOpen = false;
  activeTab = 'workflow';
  openMenuIndex: number | null = null;
  targetWorkflow: Workflow;
  mode: "CREATE" | "UPDATE" | "VIEW" = "CREATE"; 
  workflowList!: WorkflowList;
  mdaOptions: MdaOption[] = [];
  isLoadingWorkflows: boolean = false;

  readonly pageSize = 50;
  readonly emptyWorkflowFilters: WorkflowSearchFilter = {
    mdaId: '',
    workflowName: '',
    fromDate: '',
    toDate: '',
  };

  tabs = [
    { key: 'workflow', label: 'Workflow' },
    { key: 'users', label: 'User Management' },
    { key: 'roles', label: 'Role Management' },
  ];

  columns = [
    'Workflow Name',
    'Description',
    'Mda Name',
    'No of Steps',
    'Date Created',
    'Status',
    'Actions',
  ];
  workflowFilters: WorkflowSearchFilter = { ...this.emptyWorkflowFilters };
  workflowFilterFields: FilterDrawerFieldConfig[] = [];

  authenticationService:  AuthenticationService = inject(AuthenticationService);

  constructor(private apiService: ApiServiceService) {}
  
  ngOnInit(): void {
    this.buildWorkflowFilterFields();
    this.getMdas();
    this.fetchWorkflows();
  }

  get activeFilterCount(): number {
    return Object.values(this.workflowFilters).filter((value) => value.trim().length > 0)
      .length;
  }

  setActiveTab(tab: string): void {
    this.activeTab = tab;
    this.openMenuIndex = null;
  }

  getTabClass(tab: string): string {
    if (tab === this.activeTab) {
      return 'bg-teal-600 text-white shadow-sm';
    }
    return 'text-slate-500 hover:text-slate-700 hover:bg-slate-100';
  }

  getStatusClass(status: string): string {
    const normalizedStatus = status.toUpperCase();

    if (normalizedStatus === 'ACTIVE') {
      return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
    }

    if (normalizedStatus === 'INACTIVE') {
      return 'bg-slate-100 text-slate-500 border border-slate-200';
    }

    return 'bg-slate-100 text-slate-500 border border-slate-200';
  }

  fetchWorkflows(page = 1): void {
    this.isLoadingWorkflows = true;
    const data = {
      ...this.buildWorkflowSearchPayload(),
      page,
      pageSize: this.pageSize,
    };

    this.apiService.post('/workflows/search', data).subscribe({
      next: (res: WorkflowList) => {
        this.workflowList = res;
      },
      error: (err) => {
        console.error('Search failed', err);
      },
      complete: () => {
        this.isLoadingWorkflows = false;
      }
    });
  }

  toggleMenu(index: number): void {
    this.openMenuIndex = this.openMenuIndex === index ? null : index;
  }

  openCreateModal(): void {
    this.isModalOpen = true;
    this.mode = "CREATE";
  }

  onEdit(wf: Workflow): void {
    this.openMenuIndex = null;
    this.targetWorkflow = wf;
    this.mode = "UPDATE";
    this.isModalOpen = true;
    console.log('Edit workflow:', wf.id);
  }

  onView(wf: Workflow): void {
    this.openMenuIndex = null;
    this.targetWorkflow = wf;
    this.mode = "VIEW";
    this.isModalOpen = true;
    console.log('Edit workflow:', wf.id);
  }

  onDelete(index: number): void {
    const workflow = this.workflowList.data.content[index];

    if (!workflow) {
      alertify.error("Failed to get workflow information");
      return;
    }

    const params = new HttpParams()
      .set("id", workflow.id);
    this.apiService.delete("/delete/workflows", {params}).subscribe(
      {
        next: (response: GenericResponse) => {
          if (response.status === "success") {
            alertify.success(response.message);
          } else {
            alertify.error(response.message);
          }
        },
        error: (error) => {
          if (error instanceof HttpErrorResponse) {
            alertify.error(error.message);
          } else {
            alertify.error(JSON.stringify(error));
          }
        },
        complete: () => {
          this.fetchWorkflows();
        }
      }
    );
  }

  openFilters(): void {
    this.openMenuIndex = null;
    this.isFilterDrawerOpen = true;
  }

  closeFilters(): void {
    this.isFilterDrawerOpen = false;
  }

  applyFilters(rawFilters: FilterDrawerValue): void {
    this.workflowFilters = {
      ...this.emptyWorkflowFilters,
      ...Object.fromEntries(
        Object.entries(rawFilters).map(([key, value]) => [key, `${value ?? ''}`]),
      ),
    };

    this.isFilterDrawerOpen = false;
    this.fetchWorkflows(1);
  }

  getMdas(): void {
    this.apiService.get('/mdas').subscribe({
      next: (response: MdaListResponse) => {
        this.mdaOptions = response.data ?? [];
        this.buildWorkflowFilterFields();
      },
      error: () => {
        this.mdaOptions = [];
        this.buildWorkflowFilterFields();
      },
    });
  }

  private buildWorkflowSearchPayload(): WorkflowSearchFilter {
    return {
      mdaId: this.workflowFilters.mdaId.trim(),
      workflowName: this.workflowFilters.workflowName.trim(),
      fromDate: this.workflowFilters.fromDate.trim(),
      toDate: this.workflowFilters.toDate.trim(),
    };
  }

  private buildWorkflowFilterFields(): void {
    this.workflowFilterFields = [
      {
        key: 'workflowName',
        label: 'Workflow Name',
        type: 'text',
        placeholder: 'Filter by workflow name',
      },
      {
        key: 'mdaId',
        label: 'MDA',
        type: 'searchable-select',
        placeholder: 'Select MDA',
        searchablePlaceholder: 'Search MDA options',
        options: this.mdaOptions.map((option) => ({
          label: option.name,
          value: option.id,
          description: option.code,
        })),
      },
      {
        key: 'fromDate',
        label: 'From Date',
        type: 'date',
      },
      {
        key: 'toDate',
        label: 'To Date',
        type: 'date',
      },
    ];
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.openMenuIndex = null;
    this.closeFilters();
  }
  onWorkflowSaved(event: any) {
    this.isModalOpen = false;
    this.fetchWorkflows();
  }
}
